import { Box, Card, FormControl, FormHelperText, Grid, IconButton, ListItemText, MenuItem, Select, TextField, Typography } from "@mui/material";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { KnowledgeDocumentListItem } from "src/types/knowledgeDocument";
import { useFormikContext } from "formik";

interface KnowledgeDocumentRowProps {
    document: any;
    index: number;
    availableDocuments: KnowledgeDocumentListItem[];
    onDelete: () => void;
    onDocumentSelect: (documentId: string) => void;
    assignedDocumentIds: string[];
    disabled?: boolean;
}

const KnowledgeDocumentRow: FC<KnowledgeDocumentRowProps> = (props) => {
    const {
        document,
        index,
        availableDocuments,
        onDelete,
        onDocumentSelect,
        assignedDocumentIds,
        disabled,
    } = props;
    const { t } = useTranslation();
    const { handleChange, handleBlur, errors, touched } = useFormikContext<any>();

    const rowErrors = (errors.knowledgeDocuments as any)?.[index];
    const rowTouched = (touched.knowledgeDocuments as any)?.[index];

    const isDocumentDisabled = (docId: string) =>
        assignedDocumentIds.includes(docId) && docId !== document.knowledgeDocumentId;

    const isOrphaned = document.knowledgeDocumentId
        && !availableDocuments.some((d) => d.id === document.knowledgeDocumentId);

    const handleDocumentChange = (e: any) => {
        handleChange(e);
        onDocumentSelect(e.target.value);
    };

    return (
        <Card
            variant="outlined"
            sx={{
                mb: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
            }}
            data-testid={`knowledge-doc-${index}`}
        >
            <Grid container alignItems="stretch">
                {/* Document selector column */}
                <Grid item xs={5} sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                        {t("AiAgentList.KnowledgeDocuments.DocumentLabel")}
                    </Typography>
                    <FormControl fullWidth>
                        <Select
                            id={`knowledge-doc-select-${index}`}
                            value={availableDocuments.length > 0 || isOrphaned ? (document.knowledgeDocumentId ?? "") : ""}
                            error={!!rowTouched?.knowledgeDocumentId && !!rowErrors?.knowledgeDocumentId}
                            onChange={handleDocumentChange}
                            onBlur={handleBlur}
                            size="small"
                            name={`knowledgeDocuments.${index}.knowledgeDocumentId`}
                            data-testid={`knowledge-doc-select-${index}`}
                            disabled={disabled}
                        >
                            <MenuItem value="">
                                <ListItemText primary={t("AiAgentList.KnowledgeDocuments.SelectDocument")} />
                            </MenuItem>
                            {availableDocuments.length === 0 && !isOrphaned && (
                                <MenuItem disabled>
                                    <ListItemText primary={t("AiAgentList.KnowledgeDocuments.NoDocumentsAvailable")} />
                                </MenuItem>
                            )}
                            {isOrphaned && (
                                <MenuItem value={document.knowledgeDocumentId} disabled>
                                    {document.knowledgeDocumentName || t("AiAgentList.KnowledgeDocuments.DeletedDocument")}
                                </MenuItem>
                            )}
                            {availableDocuments.map((item) => (
                                <MenuItem
                                    key={item.id}
                                    value={item.id}
                                    disabled={isDocumentDisabled(item.id)}
                                >
                                    {item.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {isOrphaned && (
                            <FormHelperText sx={{ display: "flex", alignItems: "center", gap: 0.5 }} error>
                                <WarningAmberIcon fontSize="inherit" />
                                {t("AiAgentList.KnowledgeDocuments.OrphanedWarning")}
                            </FormHelperText>
                        )}
                        <FormHelperText error>
                            {!!rowTouched?.knowledgeDocumentId && rowErrors ? rowErrors.knowledgeDocumentId : null}
                        </FormHelperText>
                    </FormControl>
                </Grid>

                {/* Description column */}
                <Grid item xs={6} sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                        {t("AiAgentList.KnowledgeDocuments.DescriptionLabel")}{" "}
                        <Box component="span" sx={{ fontStyle: "italic", color: "text.disabled" }}>
                            {t("AiAgentList.KnowledgeDocuments.ReadOnly")}
                        </Box>
                    </Typography>
                    <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        value={document.description || ""}
                        variant="outlined"
                        disabled
                        placeholder={t("AiAgentList.KnowledgeDocuments.DescriptionPlaceholder")}
                        data-testid={`knowledge-doc-description-${index}`}
                    />
                </Grid>

                {/* Delete column — vertically centered */}
                <Grid
                    item
                    xs={1}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <IconButton
                        onClick={onDelete}
                        data-testid={`knowledge-doc-delete-${index}`}
                        title={t("AiAgentList.KnowledgeDocuments.RemoveDocument")}
                        size="small"
                        disabled={disabled}
                        sx={{
                            color: "text.disabled",
                            borderRadius: 1,
                        }}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Grid>
            </Grid>
        </Card>
    );
};

export default KnowledgeDocumentRow;
